import type { FrontendErrorPayload } from "@/frontend/types/errors";

/**
 * Browser notifications that are not bugs, matched as substrings and dropped
 * before anything is sent. Both spellings below mean an observed element resized
 * again inside one delivery cycle, which the virtualized record lists do
 * routinely, and relaying it would bury real errors in Cloud Error Reporting.
 */
const IGNORED_ERROR_MESSAGES = [
  // Chromium's current wording.
  "ResizeObserver loop completed with undelivered notifications",
  // Older Chromium and Safari.
  "ResizeObserver loop limit exceeded",
];

/**
 * True when a message is a known benign browser notification.
 *
 * @param message The error message as the browser reported it.
 */
function isIgnoredError(message: string): boolean {
  return IGNORED_ERROR_MESSAGES.some((ignored) => message.includes(ignored));
}

/**
 * Sends a frontend error to the server for logging in Cloud Error Reporting.
 * Known benign browser notifications are dropped here rather than at the call
 * sites, so every path into reporting is filtered. Fetch failures are swallowed
 * because retrying would risk a loop. Server-side rate limit (`consumeRateLimit`
 * with tier `frontend_error`) bounds flooding.
 */
export function reportFrontendError(payload: FrontendErrorPayload): void {
  if (isIgnoredError(payload.message)) return;

  const body = JSON.stringify({
    message: payload.message,
    stack: payload.stack,
    url: payload.url ?? window.location.href,
    componentStack: payload.componentStack,
  });

  fetch("/api/_errors/report", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => { /* intentional no-op */ });
}

/**
 * Installs window handlers that forward uncaught errors + unhandled promise
 * rejections to the server. Call once at app startup, before any React render.
 */
export function initErrorReporter(): void {
  window.addEventListener("error", (event: ErrorEvent) => {
    reportFrontendError({
      message: event.message || event.error?.message || "unknown error",
      stack: event.error?.stack ?? "",
    });
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    reportFrontendError({
      message: reason?.message ?? String(reason ?? "unhandled promise rejection"),
      stack: reason?.stack ?? "",
    });
  });
}
